import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  Search,
  ShoppingCart,
  Package,
  Building2,
  Calendar,
  FileText,
  Calculator,
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileUpload } from '@/components/ui/file-upload';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface OrderItemForm {
  id?: string;
  productId: string;
  productName?: string;
  productCode?: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  notes?: string;
}

interface PurchaseOrderForm {
  supplierId: string;
  warehouseId: string;
  orderDate: string;
  expectedDate?: string;
  currency: string;
  paymentTerms?: string;
  deliveryTerms?: string;
  notes?: string;
  items: OrderItemForm[];
}

interface SupplierProduct {
  id: string;
  productId: string;
  unitCost: number;
  currency: string;
}

export function PurchaseOrderEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const [productSearch, setProductSearch] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceFile, setInvoiceFile] = useState<{ fileUrl: string; fileKey: string } | null>(null);
  const [existingInvoiceId, setExistingInvoiceId] = useState<string | null>(null);

  // Fetch order data
  const { data: order, isLoading: isLoadingOrder, error: orderError } = trpc.tradingPurchaseOrder.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
  } = useForm<PurchaseOrderForm>({
    defaultValues: {
      currency: 'EUR',
      items: [],
    },
  });

  const { fields: items, append: appendItem, remove: removeItem } = useFieldArray({
    control,
    name: 'items',
  });

  // Queries
  const { data: suppliersData } = trpc.tradingSupplier.list.useQuery({});
  const { data: warehousesData } = trpc.warehouse.list.useQuery({});
  const { data: productsData } = trpc.tradingProduct.list.useQuery(
    { search: productSearch },
    { enabled: productSearch.length >= 2 }
  );

  const suppliers = suppliersData?.suppliers ?? [];
  const warehouses = warehousesData ?? [];
  const products = productsData?.products ?? [];

  const watchedValues = watch();
  const selectedSupplierId = watchedValues.supplierId;
  const selectedWarehouseId = watchedValues.warehouseId;

  // Get supplier products for pricing hints
  const { data: supplierProductsData } = trpc.tradingSupplierProduct.list.useQuery(
    { supplierId: selectedSupplierId },
    { enabled: !!selectedSupplierId }
  );

  const supplierProducts = supplierProductsData?.supplierProducts ?? [];

  // Initialize form with order data
  useEffect(() => {
    if (order && !isInitialized) {
      reset({
        supplierId: order.supplierId,
        warehouseId: order.warehouseId,
        orderDate: format(new Date(order.orderDate), 'yyyy-MM-dd'),
        expectedDate: order.expectedDate ? format(new Date(order.expectedDate), 'yyyy-MM-dd') : undefined,
        currency: order.currency,
        paymentTerms: order.paymentTerms || undefined,
        deliveryTerms: order.deliveryTerms || undefined,
        notes: order.notes || undefined,
        items: order.items.map((item) => ({
          id: item.id,
          productId: item.productId,
          productName: item.productName,
          productCode: item.productCode,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate,
          notes: item.notes || undefined,
        })),
      });
      // Initialize invoice data
      const invoice = order.supplierInvoices?.[0];
      if (invoice) {
        setExistingInvoiceId(invoice.id);
        setInvoiceNumber(invoice.invoiceNumber || '');
        if (invoice.fileKey) {
          setInvoiceFile({ fileUrl: invoice.fileUrl || '', fileKey: invoice.fileKey });
        }
      }
      setIsInitialized(true);
    }
  }, [order, isInitialized, reset]);

  const utils = trpc.useUtils();

  // Update mutations
  const updateMutation = trpc.tradingPurchaseOrder.update.useMutation({
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateInvoiceMutation = trpc.tradingSupplierInvoice.update.useMutation({
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const createInvoiceMutation = trpc.tradingSupplierInvoice.create.useMutation({
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateItemsMutation = trpc.tradingPurchaseOrder.updateItems.useMutation({
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  // Calculate totals
  const { subtotal, taxAmount, total } = useMemo(() => {
    let subtotal = 0;
    let taxAmount = 0;

    watchedValues.items?.forEach((item) => {
      const lineSubtotal = (item.quantity || 0) * (item.unitPrice || 0);
      const lineTax = lineSubtotal * ((item.taxRate || 0) / 100);
      subtotal += lineSubtotal;
      taxAmount += lineTax;
    });

    return {
      subtotal,
      taxAmount,
      total: subtotal + taxAmount,
    };
  }, [watchedValues.items]);

  const handleAddProduct = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    // Check if product already exists in items
    const existingIndex = items.findIndex((item) => item.productId === productId);
    if (existingIndex >= 0) {
      // Increment quantity instead of adding new line
      const currentQty = watchedValues.items[existingIndex]?.quantity || 0;
      setValue(`items.${existingIndex}.quantity`, currentQty + 1);
      return;
    }

    // Get supplier price if available
    const supplierProduct = supplierProducts.find((sp: SupplierProduct) => sp.productId === productId);
    const unitPrice = supplierProduct?.unitCost ?? 0;

    appendItem({
      productId: product.id,
      productName: product.name,
      productCode: product.fullCode,
      quantity: 1,
      unitPrice,
      taxRate: 0,
      notes: '',
    });

    setProductSearch('');
  };

  const onSubmit = async (data: PurchaseOrderForm) => {
    if (!id) return;

    if (!data.supplierId) {
      showErrorToast(t('purchasing.validation.selectSupplier'));
      return;
    }

    if (!data.warehouseId) {
      showErrorToast(t('purchasing.validation.selectWarehouse'));
      return;
    }

    if (data.items.length === 0) {
      showErrorToast(t('purchasing.validation.addProduct'));
      return;
    }

    try {
      // First update the order details
      await updateMutation.mutateAsync({
        id,
        data: {
          supplierId: data.supplierId,
          warehouseId: data.warehouseId,
          orderDate: data.orderDate ? new Date(data.orderDate) : undefined,
          expectedDate: data.expectedDate ? new Date(data.expectedDate) : null,
          paymentTerms: data.paymentTerms || null,
          deliveryTerms: data.deliveryTerms || null,
          notes: data.notes || null,
          currency: data.currency,
        },
      });

      // Then update the items
      await updateItemsMutation.mutateAsync({
        orderId: id,
        items: data.items.map((item) => ({
          id: item.id,
          productId: item.productId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate,
          notes: item.notes || undefined,
        })),
      });

      // Update or create supplier invoice
      if (invoiceNumber.trim()) {
        if (existingInvoiceId) {
          await updateInvoiceMutation.mutateAsync({
            id: existingInvoiceId,
            invoiceNumber: invoiceNumber.trim(),
            fileUrl: invoiceFile?.fileUrl || null,
            fileKey: invoiceFile?.fileKey || null,
          });
        } else {
          await createInvoiceMutation.mutateAsync({
            supplierId: data.supplierId,
            invoiceNumber: invoiceNumber.trim(),
            purchaseOrderId: id,
            invoiceDate: new Date(),
            dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            subtotal: subtotal,
            taxAmount: taxAmount,
            total: total,
            currency: data.currency,
            fileUrl: invoiceFile?.fileUrl || undefined,
            fileKey: invoiceFile?.fileKey || undefined,
          });
        }
      }

      // Navigate after all mutations succeed
      utils.tradingPurchaseOrder.byId.invalidate(id);
      utils.tradingPurchaseOrder.list.invalidate();
      showSuccessToast(t('common.saved'));
      navigate(`/trading/purchasing/${id}`);
    } catch {
      // Error handling is done in mutation callbacks
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const selectedSupplier = suppliers.find((s) => s.id === selectedSupplierId);
  const selectedWarehouse = warehouses.find((w) => w.id === selectedWarehouseId);

  // Loading state
  if (isLoadingOrder) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  // Error state
  if (orderError || !order) {
    return (
      <div className="space-y-4">
        <Link
          to="/trading/purchasing"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </Link>
        <div className="border border-red-300 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">{orderError?.message || t('purchasing.purchaseOrders.notFound')}</p>
        </div>
      </div>
    );
  }


  const isPending = updateMutation.isPending || updateItemsMutation.isPending || updateInvoiceMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={`/trading/purchasing/${id}`}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{t('purchasing.purchaseOrders.editOrder')}</h1>
            <p className="text-muted-foreground font-mono">{order.orderNumber}</p>
          </div>
        </div>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={isPending}
        >
          {isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {t('common.save')}
        </Button>
      </div>

      <form className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Supplier & Warehouse Section */}
          <div className="border-border border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Building2 className="h-5 w-5" />
              {t('purchasing.purchaseOrders.supplier')} & {t('purchasing.purchaseOrders.warehouse')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('purchasing.purchaseOrders.supplier')} *</Label>
                <Combobox
                  options={suppliers.map((s) => ({
                    value: s.id,
                    label: s.companyName,
                  }))}
                  value={selectedSupplierId || ''}
                  onValueChange={(value) => setValue('supplierId', value)}
                  placeholder={t('purchasing.purchaseOrders.selectSupplier')}
                  searchPlaceholder={t('purchasing.suppliers.searchPlaceholder')}
                />
                {selectedSupplier && (
                  <p className="text-xs text-muted-foreground">
                    {selectedSupplier.contactName} - {selectedSupplier.email}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('purchasing.purchaseOrders.warehouse')} *</Label>
                <Combobox
                  options={warehouses.map((w) => ({
                    value: w.id,
                    label: `${w.code} - ${w.name}`,
                  }))}
                  value={selectedWarehouseId || ''}
                  onValueChange={(value) => setValue('warehouseId', value)}
                  placeholder={t('inventory.stockLevels.selectWarehouse')}
                  searchPlaceholder={t('warehouses.searchPlaceholder')}
                />
                {selectedWarehouse && (
                  <p className="text-xs text-muted-foreground">
                    {selectedWarehouse.city}, {selectedWarehouse.country}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div className="border-border border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Calendar className="h-5 w-5" />
              {t('purchasing.purchaseOrders.orderDate')} & {t('purchasing.purchaseOrders.expectedDate')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="orderDate">{t('purchasing.purchaseOrders.orderDate')} *</Label>
                <Input
                  type="date"
                  id="orderDate"
                  {...register('orderDate', { required: true })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expectedDate">{t('purchasing.purchaseOrders.expectedDate')}</Label>
                <Input
                  type="date"
                  id="expectedDate"
                  {...register('expectedDate')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('purchasing.purchaseOrders.currency')}</Label>
                <Combobox
                  options={[
                    { value: 'EUR', label: 'EUR' },
                    { value: 'USD', label: 'USD' },
                    { value: 'GBP', label: 'GBP' },
                    { value: 'TRY', label: 'TRY' },
                  ]}
                  value={watchedValues.currency}
                  onValueChange={(value) => setValue('currency', value)}
                  placeholder={t('purchasing.purchaseOrders.currency')}
                  searchPlaceholder="Search..."
                  disabled
                />
                <p className="text-xs text-muted-foreground">{t('purchasing.purchaseOrders.currencyNotEditable')}</p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="border-border border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="flex items-center gap-2 font-semibold">
                <Package className="h-5 w-5" />
                {t('purchasing.purchaseOrders.items')} ({items.length})
              </h2>
            </div>

            {/* Product Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('products.searchPlaceholder')}
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {productSearch.length === 0 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t('purchasing.purchaseOrders.searchHint')}
                </p>
              )}

              {productSearch.length > 0 && productSearch.length < 2 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t('purchasing.purchaseOrders.typeMoreChars')}
                </p>
              )}

              {productSearch.length >= 2 && products.length > 0 && (
                <div className="mt-2 border-border border divide-y max-h-48 overflow-y-auto">
                  {products.map((product) => {
                    const supplierProduct = supplierProducts.find((sp: SupplierProduct) => sp.productId === product.id);
                    return (
                      <button
                        key={product.id}
                        type="button"
                        className="w-full p-3 text-left hover:bg-muted/50 transition-colors flex items-center justify-between"
                        onClick={() => handleAddProduct(product.id)}
                      >
                        <div>
                          <p className="font-medium text-sm">{product.name}</p>
                          <p className="text-xs text-muted-foreground">{product.fullCode}</p>
                        </div>
                        {supplierProduct && (
                          <span className="text-sm font-medium">
                            {formatCurrency(supplierProduct.unitCost, supplierProduct.currency)}
                          </span>
                        )}
                        <Plus className="h-4 w-4 text-muted-foreground" />
                      </button>
                    );
                  })}
                </div>
              )}

              {productSearch.length >= 2 && products.length === 0 && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('products.noProducts')}
                </p>
              )}
            </div>

            {/* Items List */}
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center border border-border bg-muted/30">
                <ShoppingCart className="h-10 w-10 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">
                  {t('purchasing.purchaseOrders.noOrders')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('purchasing.purchaseOrders.searchAndAddProducts')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((field, index) => {
                  const item = watchedValues.items[index];
                  const lineSubtotal = (item?.quantity || 0) * (item?.unitPrice || 0);
                  const lineTax = lineSubtotal * ((item?.taxRate || 0) / 100);
                  const lineTotal = lineSubtotal + lineTax;

                  return (
                    <div key={field.id} className="border-border border p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium">{field.productName}</p>
                          <p className="text-xs text-muted-foreground font-mono">{field.productCode}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-5">
                        <div className="space-y-1">
                          <Label className="text-xs">{t('purchasing.goodsReceipts.orderedQty')}</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.quantity`, { valueAsNumber: true })}
                            min="1"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('purchasing.supplierProducts.unitCost')}</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.unitPrice`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('purchasing.purchaseOrders.tax')} %</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.taxRate`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                            max="100"
                          />
                        </div>
                        <div className="space-y-1 sm:col-span-2">
                          <Label className="text-xs">{t('purchasing.supplierProducts.notes')}</Label>
                          <Input
                            {...register(`items.${index}.notes`)}
                            placeholder={t('purchasing.purchaseOrders.optionalNotes')}
                          />
                        </div>
                      </div>

                      <div className="flex justify-end mt-3 pt-3 border-t">
                        <span className="text-sm">
                          <span className="text-muted-foreground">{t('purchasing.purchaseOrders.lineTotal_label')}</span>
                          <span className="font-medium">
                            {formatCurrency(lineTotal, watchedValues.currency)}
                          </span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Invoice Information */}
          <div className="border-border border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <FileText className="h-5 w-5" />
              {t('purchasing.purchaseOrders.invoiceInfo')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="invoiceNumber">{t('purchasing.purchaseOrders.invoiceNumber')}</Label>
                <Input
                  id="invoiceNumber"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder={t('purchasing.purchaseOrders.invoiceNumberPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('purchasing.purchaseOrders.invoiceFile')}</Label>
                {invoiceFile ? (
                  <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
                    <FileText className="h-4 w-4 text-green-600 shrink-0" />
                    <span className="text-sm flex-1 truncate">{t('purchasing.purchaseOrders.fileUploaded')}</span>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setInvoiceFile(null)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <FileUpload
                    folder="supplier-invoices"
                    accept=".pdf,.jpg,.jpeg,.png"
                    maxSizeMB={10}
                    onUploaded={(result) => setInvoiceFile({ fileUrl: result.fileUrl, fileKey: result.fileKey })}
                    onRemoved={() => setInvoiceFile(null)}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Terms & Notes */}
          <div className="border-border border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <FileText className="h-5 w-5" />
              {t('purchasing.purchaseOrders.notes')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="paymentTerms">{t('purchasing.purchaseOrders.paymentTerms')}</Label>
                <Input
                  id="paymentTerms"
                  {...register('paymentTerms')}
                  placeholder={t('purchasing.purchaseOrders.paymentTermsPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryTerms">{t('purchasing.purchaseOrders.deliveryTerms')}</Label>
                <Input
                  id="deliveryTerms"
                  {...register('deliveryTerms')}
                  placeholder={t('purchasing.purchaseOrders.deliveryTermsPlaceholder')}
                />
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <Label htmlFor="notes">{t('purchasing.purchaseOrders.notes')}</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder={t('purchasing.purchaseOrders.notesPlaceholder')}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Sidebar - Order Summary */}
        <div className="space-y-6">
          <div className="border-border border bg-card p-6 sticky top-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Calculator className="h-5 w-5" />
              {t('purchasing.purchaseOrders.summary')}
            </h2>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('purchasing.purchaseOrders.orderNumber')}</span>
                <span className="font-mono font-medium">{order.orderNumber}</span>
              </div>

              {selectedSupplier && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('purchasing.purchaseOrders.supplier')}</span>
                  <span className="font-medium">{selectedSupplier.companyName}</span>
                </div>
              )}

              {selectedWarehouse && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('purchasing.purchaseOrders.warehouse')}</span>
                  <span className="font-medium">{selectedWarehouse.code}</span>
                </div>
              )}

              {watchedValues.orderDate && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('purchasing.purchaseOrders.orderDate')}</span>
                  <span className="font-medium">
                    {format(new Date(watchedValues.orderDate), 'MMM d, yyyy')}
                  </span>
                </div>
              )}

              {watchedValues.expectedDate && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('purchasing.purchaseOrders.expectedDate')}</span>
                  <span className="font-medium">
                    {format(new Date(watchedValues.expectedDate), 'MMM d, yyyy')}
                  </span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('purchasing.purchaseOrders.items')}</span>
                <span className="font-medium">{items.length}</span>
              </div>

              <div className="border-t my-3" />

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('purchasing.purchaseOrders.subtotal')}</span>
                <span>{formatCurrency(subtotal, watchedValues.currency)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('purchasing.purchaseOrders.tax')}</span>
                <span>{formatCurrency(taxAmount, watchedValues.currency)}</span>
              </div>

              <div className="flex justify-between text-base pt-2 border-t">
                <span className="font-semibold">{t('purchasing.purchaseOrders.total')}</span>
                <span className="font-bold">
                  {formatCurrency(total, watchedValues.currency)}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 space-y-2">
              <Button
                type="button"
                className="w-full"
                onClick={handleSubmit(onSubmit)}
                disabled={isPending || items.length === 0}
              >
                {isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t('common.saveChanges')}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/trading/purchasing/${id}`)}
              >
                {t('common.cancel')}
              </Button>
            </div>

            {/* Validation Warnings */}
            {(!selectedSupplierId || !selectedWarehouseId || items.length === 0) && (
              <div className="mt-4 space-y-2">
                {!selectedSupplierId && (
                  <p className="text-xs text-yellow-600">{t('purchasing.validation.selectSupplierToContinue')}</p>
                )}
                {!selectedWarehouseId && (
                  <p className="text-xs text-yellow-600">{t('purchasing.validation.selectWarehouseToContinue')}</p>
                )}
                {items.length === 0 && (
                  <p className="text-xs text-yellow-600">{t('purchasing.validation.addAtLeastOneProduct')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
